/**
 * A date column's header filter (KPR-1712, KPE-051 ruling i06): the choices it
 * offers, the conditions each one writes, and how a condition already in the
 * filter reads back as one of them.
 *
 * EVERY CHOICE IS ONE THE FILTER BUILDER ALREADY HAS, WRITTEN THE SAME WAY:
 *   Today, This month   the calendar periods of utils/datePresets
 *                       (`>= the day and < the next`, absolute, the browser's
 *                       own calendar day, the upper bound exclusive)
 *   Last 7 days         the preset's point on a new date row's operator: the
 *                       builder's row starts on `gt`, so `> -7d`
 *   Overdue             `< today and is not empty`. Offered on every date and
 *                       date-time element, as the builder offers it, and never
 *                       on Created or Updated, which keepr stamps and so are
 *                       never after today (the owner's ruling, KPR-1712 Q1)
 *   Custom range        Between…: `>= from and < the day after to`
 *                       (datePresets `betweenPart`), both days required
 *
 * `now` is injectable so a test can stand on a fixed day; the default is the
 * moment of the call, which for a write is the moment of Apply, as for the
 * builder.
 */
import dayjs from 'dayjs';

import { betweenPart, presetParts, presetPoint, recognizeSpan } from './datePresets';
import { makeValueNode } from './kqlBuilder';

export const DATE_RANGE = 'range';
const LAST_7_DAYS = 'last-7d';
const PERIODS = new Set(['today', 'this-month']);
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

const CHOICES = [
	{ value: 'today', label: 'Today' },
	{ value: LAST_7_DAYS, label: 'Last 7 days' },
	{ value: 'this-month', label: 'This month' },
	{ value: 'overdue', label: 'Overdue' },
	{ value: DATE_RANGE, label: 'Custom range' },
];

/** The choices a date column's header offers, in order. */
export const dateChoicesFor = column => CHOICES.filter(choice => choice.value !== 'overdue' || Boolean(column?.overdue));

/** A date draft with nothing chosen. */
export const emptyDateDraft = () => ({ preset: null, from: null, to: null });

const isDay = text => typeof text === 'string' && DAY_RE.test(text) && dayjs(text).isValid();

/** The conditions a date draft stands for, `[{ cmp, value? }]`: none until it asks for something whole. */
export function dateDraftConditions(column, draft, now) {
	const dataType = column?.field?.dataType;
	const preset = draft?.preset;
	if (!preset) return [];
	if (PERIODS.has(preset) || (preset === 'overdue' && column?.overdue)) {
		return presetParts(preset, now === undefined ? dayjs() : now) || [];
	}
	if (preset === LAST_7_DAYS) {
		// The builder's row: a fresh date row's first operator, the preset's point.
		const value = makeValueNode(dataType, 'gt', presetPoint(LAST_7_DAYS));
		return value ? [{ cmp: 'gt', value }] : [];
	}
	if (preset === DATE_RANGE) {
		// Half a span is not a filter, in the builder either.
		if (!isDay(draft.from) || !isDay(draft.to)) return [];
		return [betweenPart(0, draft.from), betweenPart(1, draft.to)];
	}
	return [];
}

/** Why a date draft cannot be applied yet, or null. */
export function dateDraftProblem(draft) {
	if (draft?.preset !== DATE_RANGE) return null;
	const from = isDay(draft.from);
	const to = isDay(draft.to);
	if (from !== to) return 'Pick both days.';
	if (from && to && draft.from > draft.to) return 'The first day is after the last.';
	return null;
}

/**
 * The column's top-level conditions (`hits`, in order) as a date draft, or
 * null when they are not a choice the header has: the builder edits those.
 *
 * A span the builder's presets name (This week, Last month…) as two whole
 * days reads as the custom range it is, and writes back as the same text. A
 * Today written on another day reads as that day's range, as the builder's
 * chip names its dates once the period has passed.
 */
export function readDateDraft(column, hits, now) {
	if (hits.length === 1) {
		const [hit] = hits;
		return hit.cmp === 'gt' && hit.value?.type === 'rel' && hit.value.v === presetPoint(LAST_7_DAYS)
			? { ...emptyDateDraft(), preset: LAST_7_DAYS }
			: null;
	}
	if (hits.length !== 2) return null;
	const [first, second] = hits;
	const span = recognizeSpan(first, second, now === undefined ? dayjs() : now);
	if (!span) return null;
	if (PERIODS.has(span.preset)) return { ...emptyDateDraft(), preset: span.preset };
	if (span.preset === 'overdue') return column?.overdue ? { ...emptyDateDraft(), preset: 'overdue' } : null;
	// Two whole days, `>=` and an exclusive `<`: the range the header writes.
	const days = first.cmp === 'gte' && second.cmp === 'lt'
		&& first.value?.type === 'date' && second.value?.type === 'date'
		&& isDay(first.value.v) && isDay(second.value.v);
	if (!days) return null;
	return {
		preset: DATE_RANGE,
		from: first.value.v,
		to: dayjs(second.value.v).subtract(1, 'day').format('YYYY-MM-DD'),
	};
}
